import { useCallback } from "react"

import {
  FetchMethod,
  useCodeerApi,
  type UrlBuilderParams,
} from "../useCodeerApi"
import type { Response } from "@/types"

export interface WebsiteCrawlJobSchema {
  id: number
  status: string
  discovered_urls: number
  downloaded_urls: number
  download_failed_urls: number
  indexed_urls: number
  indexing_failed_urls: number
  skipped_urls: number
  billing_status?: string
  billing_estimated_pages?: number
  billing_actual_pages?: number
  billing_credit_rate_per_page?: string | number
  billing_reserved_credits?: string | number
  billing_final_credits?: string | number
  finished_at?: string | null
}

export type WebsiteCrawlFailureStatus = "DOWNLOAD_FAILED" | "FAILED"

export interface WebsiteCrawlFailureSchema {
  id: number
  url: string
  path: string
  status: WebsiteCrawlFailureStatus
  error_message?: string | null
  content_preview: string
  size_bytes: number
  last_seen_at: string
  last_indexed_at?: string | null
}

export interface WebsiteCrawlFailuresSchema {
  items: WebsiteCrawlFailureSchema[]
  total: number
  limit: number
  offset: number
}

export interface WebsiteCrawlTargetSchema {
  id: number
  start_url: string
  crawl_config: Record<string, unknown>
  kb_root_missing?: boolean
  last_synced_at?: string | null
  latest_job?: WebsiteCrawlJobSchema | null
}

export interface WebsiteCrawlStateSchema {
  target?: WebsiteCrawlTargetSchema | null
}

export interface WebsiteCrawlFolderSchema {
  id: string
  knowledge_base_id: string
  name: string
  source_type?: string | null
  created_at: string
  updated_at: string
}

export interface CreateWebsiteCrawlRequest {
  start_url: string
  crawl_config?: Record<string, unknown>
}

export interface CreateWebsiteCrawlResponse {
  folder: WebsiteCrawlFolderSchema
  target: WebsiteCrawlTargetSchema
}

export interface SaveWebsiteCrawlTargetRequest {
  start_url: string
  crawl_config?: Record<string, unknown>
}

export const useGetWebsiteCrawlStateApi = () => {
  const buildUrl = useCallback(
    (params: UrlBuilderParams) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge-folders/${params.folderId}/website-crawl-state`,
    []
  )
  return useCodeerApi<Response<WebsiteCrawlStateSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge-folders/__placeholder__/website-crawl-state",
    FetchMethod.Get,
    true,
    buildUrl
  )
}

export const useGetWebsiteCrawlFailuresApi = () => {
  const buildUrl = useCallback(
    (params: UrlBuilderParams) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/website-crawl-jobs/${params.jobId}/failures`,
    []
  )
  return useCodeerApi<Response<WebsiteCrawlFailuresSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/website-crawl-jobs/__placeholder__/failures",
    FetchMethod.Get,
    true,
    buildUrl
  )
}

export const useCreateWebsiteCrawlApi = () => {
  const buildUrl = useCallback(
    (params: UrlBuilderParams) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/website-crawls`,
    []
  )
  return useCodeerApi<
    Response<CreateWebsiteCrawlResponse>,
    CreateWebsiteCrawlRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/website-crawls",
    FetchMethod.Post,
    true,
    buildUrl
  )
}

export const useSaveWebsiteCrawlTargetApi = () => {
  const buildUrl = useCallback(
    (params: UrlBuilderParams) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/website-crawls/${params.targetId}`,
    []
  )
  return useCodeerApi<
    Response<WebsiteCrawlTargetSchema>,
    SaveWebsiteCrawlTargetRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/website-crawls/__placeholder__",
    FetchMethod.Post,
    true,
    buildUrl
  )
}

export const useSyncWebsiteCrawlTargetApi = () => {
  const buildUrl = useCallback(
    (params: UrlBuilderParams) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/website-crawls/${params.targetId}/sync`,
    []
  )
  return useCodeerApi<Response<WebsiteCrawlJobSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/website-crawls/__placeholder__/sync",
    FetchMethod.Post,
    true,
    buildUrl
  )
}

export const useCancelWebsiteCrawlTargetApi = () => {
  const buildUrl = useCallback(
    (params: UrlBuilderParams) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/website-crawls/${params.targetId}/cancel`,
    []
  )
  return useCodeerApi<Response<WebsiteCrawlJobSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/website-crawls/__placeholder__/cancel",
    FetchMethod.Post,
    true,
    buildUrl
  )
}
